import { Schema } from "effect";
import { utf8ByteLength } from "./utf8";
import {
  AUTHORIZED_RESEARCH_REPORT_MAX_ENCODED_BYTES,
  ResearchBasisIndexSchema,
  ResearchBoundaryParseOptions,
  ResearchBundleV1Schema,
  ResearchReportViewSpecV1Schema,
  ResearchRunIdSchema,
  RESEARCH_STORAGE_OBJECT_MAX_BYTES,
  ResearchStorageObjectCommitSchema,
  ResearchStorageObjectKindSchema,
} from "./research";

export const RESEARCH_STORAGE_MANIFEST_MAX_BYTES = 16 * 1_024;
export const RESEARCH_STORAGE_REQUEST_MAX_BYTES =
  4 + RESEARCH_STORAGE_MANIFEST_MAX_BYTES + RESEARCH_STORAGE_OBJECT_MAX_BYTES;
export const RESEARCH_STORAGE_PUT_PATH = "/internal/v1/research/objects" as const;
export const RESEARCH_STORAGE_READ_PATH = "/internal/v1/research/report" as const;
export const RESEARCH_STORAGE_DELETE_PATH = "/internal/v1/research/delete" as const;
export const RESEARCH_STORAGE_MEDIA_TYPE = "application/vnd.papersflow.research-object.v1" as const;
export const RESEARCH_STORAGE_READ_MEDIA_TYPE =
  "application/vnd.papersflow.research-report-read.v1+json" as const;
export const RESEARCH_STORAGE_DELETE_MEDIA_TYPE =
  "application/vnd.papersflow.research-object-delete.v1+json" as const;
export const RESEARCH_STORAGE_READ_REQUEST_MAX_BYTES = 32 * 1_024;
export const RESEARCH_STORAGE_DELETE_REQUEST_MAX_BYTES = 16 * 1_024;

const ResearchStorageIdentifierSchema = Schema.String.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(180),
  Schema.makeFilter(
    (value) =>
      /^[A-Za-z0-9_-]+$/u.test(value) ||
      "must contain only characters admitted in an immutable research object path",
  ),
);
const ResearchStorageRequestIdSchema = Schema.String.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(160),
  Schema.makeFilter(
    (value) =>
      /^[A-Za-z0-9._:-]+$/u.test(value) || "must be a bounded canonical request identifier",
  ),
);
const LowercaseSha256Schema = Schema.String.check(
  Schema.makeFilter((value) => /^[a-f0-9]{64}$/u.test(value) || "must be lowercase SHA-256"),
);

export function researchStorageObjectKey(input: {
  readonly workspaceId: string;
  readonly projectId: string;
  readonly researchRunId: string;
  readonly kind: typeof ResearchStorageObjectKindSchema.Type;
}): string {
  const suffix = (() => {
    switch (input.kind) {
      case "raw-result":
        return "raw/parallel-result.json";
      case "raw-basis":
        return "raw/parallel-basis.json";
      case "canonical-bundle":
        return "canonical/research-bundle-v1.json";
      case "basis-index":
        return "canonical/citation-index-v1.json";
      case "report-spec":
        return "report/report-spec-v1.json";
      default: {
        const exhaustiveKind: never = input.kind;
        return exhaustiveKind;
      }
    }
  })();
  return `v1/workspace/workspaces/${input.workspaceId}/projects/${input.projectId}/research/${input.researchRunId}/${suffix}`;
}

/**
 * This manifest is serialized inside the HMAC-signed binary request body. No authority-bearing
 * field is carried only in an unsigned header.
 */
export const ResearchStoragePutManifestSchema = Schema.Struct({
  version: Schema.Literal("research-storage-put-v1"),
  requestId: ResearchStorageRequestIdSchema,
  workspaceId: ResearchStorageIdentifierSchema,
  projectId: ResearchStorageIdentifierSchema,
  researchRunId: ResearchRunIdSchema,
  leaseOwner: ResearchStorageIdentifierSchema,
  leaseGeneration: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1)),
  cancellationGeneration: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
  kind: ResearchStorageObjectKindSchema,
  storageObjectId: ResearchStorageIdentifierSchema,
  objectKey: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(2_048)),
  mediaType: Schema.Literal("application/json"),
  byteSize: Schema.Int.check(
    Schema.isGreaterThanOrEqualTo(1),
    Schema.isLessThanOrEqualTo(RESEARCH_STORAGE_OBJECT_MAX_BYTES),
  ),
  sha256: LowercaseSha256Schema,
}).check(
  Schema.makeFilter((manifest) =>
    manifest.objectKey === researchStorageObjectKey(manifest)
      ? true
      : "objectKey must be the deterministic research object key",
  ),
);

export const ResearchStoragePutResultSchema = Schema.Struct({
  status: Schema.Literal("verified"),
  commit: ResearchStorageObjectCommitSchema,
});

export const ResearchStorageDeleteManifestSchema = Schema.Struct({
  version: Schema.Literal("research-storage-delete-v1"),
  requestId: ResearchStorageRequestIdSchema,
  workspaceId: ResearchStorageIdentifierSchema,
  projectId: ResearchStorageIdentifierSchema,
  researchRunId: ResearchRunIdSchema,
  cleanupJobId: ResearchStorageIdentifierSchema,
  kind: ResearchStorageObjectKindSchema,
  storageObjectId: ResearchStorageIdentifierSchema,
  objectKey: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(2_048)),
}).check(
  Schema.makeFilter((manifest) =>
    manifest.objectKey === researchStorageObjectKey(manifest)
      ? true
      : "objectKey must be the deterministic research object key",
  ),
);
export const ResearchStorageDeleteResultSchema = Schema.Struct({
  status: Schema.Literal("deleted"),
});

const ResearchStorageReadObjectSchema = Schema.Struct({
  kind: Schema.Literals(["canonical-bundle", "basis-index", "report-spec"]),
  storageObjectId: ResearchStorageIdentifierSchema,
  objectKey: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(2_048)),
  mediaType: Schema.Literal("application/json"),
  byteSize: Schema.Int.check(
    Schema.isGreaterThanOrEqualTo(1),
    Schema.isLessThanOrEqualTo(AUTHORIZED_RESEARCH_REPORT_MAX_ENCODED_BYTES),
  ),
  sha256: LowercaseSha256Schema,
  etag: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(512)),
});

/** Every authority-bearing object identity is inside the exact HMAC-signed request body. */
export const ResearchStorageReadManifestSchema = Schema.Struct({
  version: Schema.Literal("research-storage-read-v1"),
  requestId: ResearchStorageRequestIdSchema,
  workspaceId: ResearchStorageIdentifierSchema,
  projectId: ResearchStorageIdentifierSchema,
  researchRunId: ResearchRunIdSchema,
  artifactRevisionId: ResearchStorageIdentifierSchema,
  objects: Schema.Array(ResearchStorageReadObjectSchema).check(
    Schema.isMinLength(3),
    Schema.isMaxLength(3),
  ),
}).check(
  Schema.makeFilter((manifest) => {
    const kinds = new Set(manifest.objects.map(({ kind }) => kind));
    if (kinds.size !== 3) return "must contain one canonical object of each kind";
    return manifest.objects.every(
      (object) =>
        object.objectKey ===
        researchStorageObjectKey({
          workspaceId: manifest.workspaceId,
          projectId: manifest.projectId,
          researchRunId: manifest.researchRunId,
          kind: object.kind,
        }),
    )
      ? true
      : "every objectKey must match its deterministic research object key";
  }),
);

export const ResearchStorageReadResultSchema = Schema.Struct({
  status: Schema.Literal("ready"),
  bundle: ResearchBundleV1Schema,
  basisIndex: ResearchBasisIndexSchema,
  report: ResearchReportViewSpecV1Schema,
}).check(
  Schema.makeFilter((value) => {
    try {
      return utf8ByteLength(JSON.stringify(value)) <= AUTHORIZED_RESEARCH_REPORT_MAX_ENCODED_BYTES
        ? true
        : "authorized research report response exceeds its byte bound";
    } catch {
      return "authorized research report response must be JSON encodable";
    }
  }),
);

export type ResearchStoragePutManifest = typeof ResearchStoragePutManifestSchema.Type;
export type ResearchStoragePutManifestEncoded = typeof ResearchStoragePutManifestSchema.Encoded;
export type ResearchStoragePutResult = typeof ResearchStoragePutResultSchema.Type;
export type ResearchStoragePutResultEncoded = typeof ResearchStoragePutResultSchema.Encoded;
export type ResearchStorageDeleteManifest = typeof ResearchStorageDeleteManifestSchema.Type;
export type ResearchStorageDeleteManifestEncoded =
  typeof ResearchStorageDeleteManifestSchema.Encoded;
export type ResearchStorageDeleteResult = typeof ResearchStorageDeleteResultSchema.Type;
export type ResearchStorageDeleteResultEncoded = typeof ResearchStorageDeleteResultSchema.Encoded;
export type ResearchStorageReadManifest = typeof ResearchStorageReadManifestSchema.Type;
export type ResearchStorageReadManifestEncoded = typeof ResearchStorageReadManifestSchema.Encoded;
export type ResearchStorageReadResult = typeof ResearchStorageReadResultSchema.Type;
export type ResearchStorageReadResultEncoded = typeof ResearchStorageReadResultSchema.Encoded;

export const decodeResearchStoragePutManifest = Schema.decodeUnknownSync(
  ResearchStoragePutManifestSchema,
  ResearchBoundaryParseOptions,
);
export const decodeResearchStoragePutResult = Schema.decodeUnknownSync(
  ResearchStoragePutResultSchema,
  ResearchBoundaryParseOptions,
);
export const decodeResearchStorageDeleteManifest = Schema.decodeUnknownSync(
  ResearchStorageDeleteManifestSchema,
  ResearchBoundaryParseOptions,
);
export const decodeResearchStorageDeleteResult = Schema.decodeUnknownSync(
  ResearchStorageDeleteResultSchema,
  ResearchBoundaryParseOptions,
);
export const decodeResearchStorageReadManifest = Schema.decodeUnknownSync(
  ResearchStorageReadManifestSchema,
  ResearchBoundaryParseOptions,
);
export const decodeResearchStorageReadResult = Schema.decodeUnknownSync(
  ResearchStorageReadResultSchema,
  ResearchBoundaryParseOptions,
);

function encodeAscii(value: string): Uint8Array<ArrayBuffer> {
  const bytes = new Uint8Array(new ArrayBuffer(value.length));
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code > 0x7f) throw new TypeError("Research object manifest must contain only ASCII");
    bytes[index] = code;
  }
  return bytes;
}

function decodeAscii(value: Uint8Array): string {
  let decoded = "";
  for (const byte of value) {
    if (byte > 0x7f) throw new TypeError("Research object manifest must contain only ASCII");
    decoded += String.fromCharCode(byte);
  }
  return decoded;
}

function copyBytes(value: Uint8Array): Uint8Array<ArrayBuffer> {
  const copy = new Uint8Array(new ArrayBuffer(value.byteLength));
  copy.set(value);
  return copy;
}

/** Four-byte big-endian manifest length, canonical JSON manifest, then the exact JSON payload. */
export function encodeResearchStoragePutFrame(
  manifest: ResearchStoragePutManifest,
  payload: Uint8Array,
): Uint8Array<ArrayBuffer> {
  if (payload.byteLength !== manifest.byteSize || payload.byteLength === 0) {
    throw new RangeError("Research object payload size does not match its manifest");
  }
  const encodedManifest = encodeAscii(
    JSON.stringify(Schema.encodeSync(ResearchStoragePutManifestSchema)(manifest)),
  );
  if (
    encodedManifest.byteLength === 0 ||
    encodedManifest.byteLength > RESEARCH_STORAGE_MANIFEST_MAX_BYTES
  ) {
    throw new RangeError("Research object manifest exceeds its protocol bound");
  }
  const framed = new Uint8Array(
    new ArrayBuffer(4 + encodedManifest.byteLength + payload.byteLength),
  );
  new DataView(framed.buffer).setUint32(0, encodedManifest.byteLength, false);
  framed.set(encodedManifest, 4);
  framed.set(payload, 4 + encodedManifest.byteLength);
  return framed;
}

export function decodeResearchStoragePutFrame(body: Uint8Array): {
  readonly manifest: ResearchStoragePutManifest;
  readonly payload: Uint8Array<ArrayBuffer>;
} {
  if (body.byteLength < 5 || body.byteLength > RESEARCH_STORAGE_REQUEST_MAX_BYTES) {
    throw new RangeError("Research object request exceeds its protocol bound");
  }
  const manifestByteSize = new DataView(body.buffer, body.byteOffset, body.byteLength).getUint32(
    0,
    false,
  );
  if (manifestByteSize === 0 || manifestByteSize > RESEARCH_STORAGE_MANIFEST_MAX_BYTES) {
    throw new RangeError("Research object manifest exceeds its protocol bound");
  }
  const payloadOffset = 4 + manifestByteSize;
  if (payloadOffset >= body.byteLength) {
    throw new RangeError("Research object payload is missing");
  }
  const manifest = decodeResearchStoragePutManifest(
    JSON.parse(decodeAscii(body.subarray(4, payloadOffset))),
  );
  const payload = copyBytes(body.subarray(payloadOffset));
  if (payload.byteLength !== manifest.byteSize) {
    throw new RangeError("Research object payload size does not match its manifest");
  }
  return { manifest, payload };
}
